import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MeteredBillingAdmin from './MeteredBillingAdmin';
import CreditUsageOverview from './CreditUsageOverview';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Admin view for the credit system: global settings (X/Y/Z + offline beta) and
// per-operation credit costs. Editing existing operations only -- no create.
const CreditManagement = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [message, setMessage] = useState('');

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [settingsRes, operationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/credits/settings`, authHeaders),
        axios.get(`${API_URL}/api/admin/credits/operations`, authHeaders),
      ]);
      setSettings(settingsRes.data || {});
      setOperations(operationsRes.data.operations || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading credit settings:', err);
      setError('Failed to load credit settings. Please try again.');
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadData(); }, [loadData]);

  const setField = (field, value) => setSettings((s) => ({ ...s, [field]: value }));

  // A blank field means "unset", never 0. purchase_credits/purchase_price_minor
  // are nullable with a CHECK > 0, so sending 0 makes the whole save fail at the
  // database -- which used to block configuring the monthly grant alone.
  const asInt = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      const patch = {
        monthly_free_credits: asInt(settings.monthly_free_credits),
        purchase_credits: asInt(settings.purchase_credits),
        purchase_price_minor: asInt(settings.purchase_price_minor),
        purchase_enabled: !!settings.purchase_enabled,
        offline_beta_enabled: !!settings.offline_beta_enabled,
        offline_max_credits: asInt(settings.offline_max_credits),
        offline_lease_hours: asInt(settings.offline_lease_hours),
      };
      // These two are NOT NULL in the schema: leave them alone when blank
      // rather than clearing them.
      if (patch.offline_max_credits === null) delete patch.offline_max_credits;
      if (patch.offline_lease_hours === null) delete patch.offline_lease_hours;
      await axios.patch(`${API_URL}/api/admin/credits/settings`, patch, authHeaders);
      flash('Credit settings saved.');
    } catch (err) {
      console.error('Error saving credit settings:', err);
      // The endpoint returns a specific reason (e.g. purchases enabled without a
      // package); showing it beats "try again" for a request that will keep failing.
      setError(err.response?.data?.error || 'Failed to save credit settings. Please try again.');
    } finally {
      setSavingSettings(false);
    }
  };

  const setOperationCost = (operationKey, value) =>
    setOperations((ops) => ops.map((op) =>
      op.operation_key === operationKey ? { ...op, credit_cost: value } : op));

  const handleSaveOperation = async (operation) => {
    try {
      await axios.patch(`${API_URL}/api/admin/credits/operations`, {
        operation_key: operation.operation_key,
        credit_cost: parseInt(operation.credit_cost, 10) || 0,
      }, authHeaders);
      flash(`Updated cost for ${operation.display_name || operation.operation_key}.`);
    } catch (err) {
      console.error('Error updating operation cost:', err);
      setError('Failed to update operation cost. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-brand-red"></div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>
        <button onClick={loadData} className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-brand-red-dark transition text-sm font-medium">Retry</button>
      </div>
    );
  }

  const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-brand-red focus:border-transparent';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const helpClass = 'text-xs text-gray-500 dark:text-gray-400 mt-1';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Credit Management</h1>
        <button onClick={loadData} className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">Refresh</button>
      </div>

      {message && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm">{message}</div>
      )}
      {error && settings && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {/* Who is using credits, what is left, and where it went. First because
          it is the question an operator opens this page to answer. */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <CreditUsageOverview />
      </section>

      {/* Metered billing: operators enable on-demand usage before tuning
          prepaid settings or per-operation costs. */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <MeteredBillingAdmin />
      </section>

      {/* Global Settings */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Global Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className={labelClass}>Monthly free credits (X)</label>
            <input type="number" min="0" className={inputClass}
              value={settings.monthly_free_credits ?? ''}
              onChange={(e) => setField('monthly_free_credits', e.target.value)} />
            <p className={helpClass}>Applies to newly created periods only.</p>
          </div>
          <div>
            <label className={labelClass}>Purchase credits (Y)</label>
            <input type="number" min="0" className={inputClass}
              value={settings.purchase_credits ?? ''}
              onChange={(e) => setField('purchase_credits', e.target.value)} />
            <p className={helpClass}>Applies to future purchases only.</p>
          </div>
          <div>
            <label className={labelClass}>Purchase price (Z, minor units)</label>
            <input type="number" min="0" className={inputClass}
              value={settings.purchase_price_minor ?? ''}
              onChange={(e) => setField('purchase_price_minor', e.target.value)} />
            <p className={helpClass}>Applies to future purchases only.</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={!!settings.purchase_enabled}
            onChange={(e) => setField('purchase_enabled', e.target.checked)} />
          Purchases enabled
        </label>

        <h3 className="text-base font-semibold text-gray-900 dark:text-white pt-2">Offline Beta</h3>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={!!settings.offline_beta_enabled}
            onChange={(e) => setField('offline_beta_enabled', e.target.checked)} />
          Offline beta enabled
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass}>Offline max credits</label>
            <input type="number" min="0" className={inputClass}
              value={settings.offline_max_credits ?? ''}
              onChange={(e) => setField('offline_max_credits', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Offline lease hours</label>
            <input type="number" min="0" className={inputClass}
              value={settings.offline_lease_hours ?? ''}
              onChange={(e) => setField('offline_lease_hours', e.target.value)} />
          </div>
        </div>

        <div>
          <button onClick={handleSaveSettings} disabled={savingSettings}
            className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-brand-red-dark transition text-sm font-medium disabled:opacity-50">
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </section>

      {/* Operation Costs */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Operation Costs</h2>
        {operations.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No operations configured.</p>
        ) : (
          <div className="space-y-3">
            {operations.map((op) => (
              <div key={op.operation_key} className="flex items-center gap-4 border-b border-gray-100 dark:border-gray-700 pb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{op.display_name || op.operation_key}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{op.operation_key}</p>
                </div>
                <input type="number" min="0" aria-label={`Credit cost for ${op.operation_key}`}
                  className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  value={op.credit_cost ?? ''}
                  onChange={(e) => setOperationCost(op.operation_key, e.target.value)} />
                <button onClick={() => handleSaveOperation(op)}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium">
                  Save
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default CreditManagement;
